import React, { useState, useEffect, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import HighchartsReact from 'highcharts-react-official';
import { SeriesOptionsType, merge } from 'highcharts';
import Highcharts from 'highcharts';
import { arrangeDateForChart } from 'components/features/utils/util';
import {
  AreaChartLegend,
  LineChartLegend,
  marker,
} from 'components/features/utils/chartUtils';
import { commaFormat } from 'components/features/new-report/utils/formatter';
import { AccountHistories } from './PortfolioStuffingDebt';

const dataToSeries = (
  data: AccountHistories[]
): Highcharts.SeriesOptionsType[] => {
  return data.map(data => {
    return {
      type: 'area',
      turboThreshold: 15000,
      color: data.AccountColor,
      fillOpacity: 0.6,
      stacking: 'normal',
      name: data.AccountName,
      data: data.Histories.sort(
        (a, b) => a.Day.getTime() - b.Day.getTime()
      ).map(
        (datum): Highcharts.PointOptionsObject => {
          return {
            x: arrangeDateForChart(datum.Day).getTime(),
            y: datum.DebtAmount,
          };
        }
      ),
    };
  }) as SeriesOptionsType[];
};

export const PortfolioStuffingChart = ({
  accountHistories,
}: {
  accountHistories: AccountHistories[];
}): React.ReactElement => {
  const series = dataToSeries(accountHistories);
  const [options, setOptions] = useState<Highcharts.Options>({
    exporting: { enabled: false },
    legend: {
      align: 'left',
      enabled: true,
      verticalAlign: 'top',
      useHTML: true,
      itemDistance: -10,
      margin: 5,
      itemStyle: { fontSize: '10px', fontWeight: 'normal', color: '#ffffff' },
      itemHoverStyle: { color: '#cccccc' },
      className: 'override-highcharts-legend',
      labelFormatter: function() {
        if (this instanceof Highcharts.Series) {
          const name = this.name;
          const color = (() => {
            const t = this as unknown;
            if (
              typeof t === 'object' &&
              t &&
              'color' in t &&
              typeof (t as { color: unknown }).color === 'string'
            ) {
              return (t as { color: string }).color;
            }
            return '';
          })();
          if (this.type === 'area') {
            return renderToStaticMarkup(
              <AreaChartLegend color={color} lightOpacity={0.6}>
                {name}
              </AreaChartLegend>
            );
          }
          if (this.type === 'line') {
            return renderToStaticMarkup(
              <LineChartLegend color={color}>{name}</LineChartLegend>
            );
          }
        }
        return '';
      },
    },
    credits: {
      enabled: false,
    },
    rangeSelector: {
      dropdown: 'always',
      x: 8,
      y: 0,
      buttonTheme: {
        fill: 'none',
        stroke: 'none',
        'stroke-width': 0,
        r: 6,
        style: {
          textAlign: 'center',
          color: '#8e9db2',
        },
        states: {
          hover: {
            fill: '#1E293B',
            style: {
              color: 'white',
              fontWeight: 'normal',
            },
          },
          select: {
            fill: '#1E293B',
            style: {
              color: 'white',
              fontWeight: 'normal',
            },
          },
        },
      },
      inputEnabled: false,
      buttons: [
        {
          type: 'week',
          count: 2,
          text: '14日間',
          title: '14日間',
        },
        {
          type: 'month',
          count: 1,
          text: '1ヶ月',
          title: '1ヶ月',
        },
        {
          type: 'month',
          count: 3,
          text: '3ヶ月',
          title: '3ヶ月',
        },
        {
          type: 'month',
          count: 6,
          text: '6ヶ月',
          title: '6ヶ月',
        },
        {
          type: 'year',
          count: 1,
          text: '1年',
          title: '1年',
        },
        {
          type: 'all',
          text: '全期間',
          title: '全期間',
        },
      ],
      selected: 2,
      buttonPosition: { align: 'left' },
    },
    navigator: {
      enabled: false,
    },
    tooltip: {
      borderColor: 'transparent',
      useHTML: true,
      formatter: function(): string {
        const Tooltip = ({
          date,
          children,
        }: {
          date: Date;
          children: React.ReactNodeArray;
        }): React.ReactElement => {
          return (
            <>
              <div
                style={{
                  display: 'grid',
                  gap: '2px',
                  gridAutoRows: 'min-content',
                  gridAutoFlow: 'row',
                }}>
                <div style={{ paddingBottom: '3px', fontSize: '9px' }}>
                  {date.getFullYear()}/{date.getMonth() + 1}/{date.getDate()}
                </div>
                {children}
              </div>
            </>
          );
        };
        const TooltipItem = ({
          name,
          color,
          value,
        }: {
          name: string;
          color: string;
          value: number;
        }): React.ReactElement => {
          return (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'min-content min-content min-content',
                  placeItems: 'center start',
                  gap: '2px',
                }}>
                <div
                  style={{
                    borderRadius: '50%',
                    height: '5px',
                    width: '5px',
                    gridColumn: 1,
                    backgroundColor: color,
                  }}
                />
                <span style={{ fontSize: '10px', gridColumn: 2 }}>{name}:</span>
                <span
                  style={{
                    fontWeight: 'bold',
                    fontSize: '10px',
                    gridColumn: 3,
                  }}>
                  {commaFormat(value / 10000, { numberOfDecimals: 1 })}万円
                </span>
              </div>
            </>
          );
        };
        return renderToStaticMarkup(
          <Tooltip date={new Date(this.x)}>
            {(this.points ?? []).map(point => (
              <TooltipItem
                name={point.series.name}
                color={point.color.toString()}
                value={point.y}
              />
            ))}
          </Tooltip>
        );
      },
    },
    xAxis: {
      ordinal: false,
      type: 'datetime',
      gridLineWidth: 0,
      dateTimeLabelFormats: {
        year: '%Y',
        month: '%Y/%m',
        day: '%m/%d',
        week: '%m/%d',
        hour: '%m/%d',
        minute: '%m/%d',
        second: '%m/%d',
        millisecond: '%m/%d',
      },
      title: { style: { color: '#ffffff' } },
      labels: { style: { color: '#ffffff' } },
      tickColor: '#64748B',
      lineColor: '#64748B',
    },
    yAxis: {
      labels: {
        formatter: function(e): string {
          return typeof this.value === 'number'
            ? commaFormat(this.value / 10000, { numberOfDecimals: 0 })
            : '';
        },
        align: 'left',
        x: 3,
        y: 4,
        style: { color: '#ffffff' },
      },
      title: {
        text: '(万円)',
        align: 'high',
        offset: 0,
        rotation: 0,
        y: 5,
        style: { color: '#ffffff' },
      },
      gridLineColor: '#64748B',
      opposite: true,
    },
    chart: {
      animation: {
        duration: 0,
      },
      backgroundColor: '#475569',
      height: 300,
      marginBottom: 20,
    },
    plotOptions: {
      series: {
        ...marker,
        turboThreshold: 15000,
      },
      area: {
        events: {
          legendItemClick: (): false => false,
        },
        turboThreshold: 15000,
      },
      line: {
        events: {
          legendItemClick: (): false => false,
        },
        turboThreshold: 15000,
      },
    },
    series,
  });

  useEffect(() => {
    setOptions(prev => {
      return merge({ ...prev, series });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountHistories]);

  const ref = useRef<HighchartsReact.RefObject>(null);
  return (
    <>
      <HighchartsReact
        highcharts={Highcharts}
        constructorType="stockChart"
        options={options}
        ref={ref}
      />
    </>
  );
};
